/**
 * HTML ↔ plain-text round-trip for simple paragraph content fields (D1).
 *
 * Owners editing a text section should see plain text, not `<p>…</p>` markup. These
 * helpers let the editor present an HTML content field as plain paragraphs (blank
 * line between paragraphs) and re-wrap to HTML on save — the stored value stays HTML
 * so the renderer is unchanged.
 *
 * Scope: this is intentionally LOSSY for rich inline markup (links, bold, nested
 * lists). It is only wired for fields a component renders as simple paragraphs
 * (gated by the schema `html` flag), where that loss is acceptable and the win —
 * owners never see tags — is large. Fields that genuinely need structure keep their
 * raw value and are handled elsewhere (child components / dedicated editors).
 */

/** Decode the handful of HTML entities our wrap step can emit, for a clean round-trip. */
function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&') // must be last
}

/** Escape the characters that would otherwise be interpreted as markup. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;') // must be first
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Convert HTML paragraph content to plain text. Block boundaries (</p>, </div>,
 * <br>, </li>, headings) become newlines; all remaining tags are stripped. Returns
 * paragraphs separated by a single blank line.
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return ''
  // If it has no tags at all, it's already plain text — return as-is (trimmed).
  if (!/<[a-z!/][\s\S]*?>/i.test(html)) return html.trim()
  const withBreaks = html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6]|ul|ol|blockquote)\s*>/gi, '\n\n')
  const stripped = withBreaks.replace(/<[^>]+>/g, '')
  const text = decodeEntities(stripped)
  // Collapse 3+ newlines to a paragraph break; trim each line; drop leading/trailing blanks.
  return text
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Convert plain text (paragraphs separated by blank lines) back to HTML `<p>` blocks.
 * Single newlines within a paragraph become `<br>`. Empty input yields an empty string.
 */
export function plainTextToHtml(text: string | null | undefined): string {
  if (!text || !text.trim()) return ''
  const paragraphs = text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean)
  return paragraphs
    .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}
